// MivaltaLanding.tsx
import React, { useState } from "react";
import { UpdatesModal } from "../../app/shared/components/UpdatesModal";
import { TesterModal } from "../../app/shared/components/TesterModal";
import { BuildModal } from "../../app/shared/components/BuildModal";
import { supabase } from "../../lib/util/supabaseClient";
import MobileLanding from "../../app/shared/components/MobileLanding";
import DesktopLanding from "../../app/shared/components/DesktopLanding";

type UpdatesFormData = {
  email: string;
};

type TesterFormData = {
  first_name: string;
  last_name: string;
  email: string;
  sport_focus: string[];
  hardware: string[];
  other_hardware?: string;
  polar_product?: string;
  garmin_product?: string;
};

type BuildFormData = {
  first_name: string;
  last_name: string;
  email: string;
  specialty: string;
  linkedin: string;
  cv_file?: File;
};

export default function MivaltaLanding() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modal, setModal] = useState<null | "updates" | "tester" | "build">(
    null
  );

  const [updatesFormData, setUpdatesFormData] = useState<UpdatesFormData>({
    email: "",
  });

  const [testerFormData, setTesterFormData] = useState<TesterFormData>({
    first_name: "",
    last_name: "",
    email: "",
    sport_focus: [""],
    hardware: [""],
    other_hardware: "",
    polar_product: "",
    garmin_product: "",
  });

  const [buildFormData, setBuildFormData] = useState<BuildFormData>({
    first_name: "",
    last_name: "",
    email: "",
    specialty: "",
    linkedin: "",
    cv_file: undefined,
  });
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    // Handle <select multiple>
    if (e.target instanceof HTMLSelectElement && e.target.multiple) {
      const values = Array.from(e.target.selectedOptions).map(
        (opt) => opt.value
      );

      if (name in testerFormData) {
        setTesterFormData((prev) => ({ ...prev, [name]: values }));
      }
      if (name in buildFormData) {
        setBuildFormData((prev) => ({ ...prev, [name]: values }));
      }
      return;
    }

    // Handle file input (e.g. resume)
    if (files && files.length > 0) {
      if (name in buildFormData) {
        setBuildFormData((prev) => ({ ...prev, [name]: files[0] }));
      }
      return;
    }

    // Fallback for single-value fields
    if (name in updatesFormData) {
      setUpdatesFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (name in testerFormData) {
      setTesterFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (name in buildFormData) {
      setBuildFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const closeModal = () => setModal(null);

  const handleSubmitUpdates = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { email } = updatesFormData;
    const response = await fetch(
      "https://isxtaldeuhfbnlbnlmld.supabase.co/functions/v1/get_updates_forward_to_n8n",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          id: Date.now(), // or any placeholder ID
          email,
          created_at: new Date().toISOString(),
        }),
      }
    );
    console.log(response);
    setIsSubmitting(false);

    if (!response.ok) {
      alert("Something went wrong. Please try again.");
    } else {
      alert("You're on the list!");
      closeModal();
      setUpdatesFormData({ email: "" });
    }
  };

  const handleSubmitTester = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const {
      first_name,
      last_name,
      email,
      sport_focus,
      hardware,
      other_hardware,
      polar_product,
      garmin_product,
    } = testerFormData;

    const cleanedSportFocus = sport_focus.filter(Boolean).join(", ");
    const cleanedHardware = hardware.filter(Boolean).join(", ");

    const response = await fetch(
      "https://isxtaldeuhfbnlbnlmld.supabase.co/functions/v1/join_tester_forward_to_n8n",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          id: Date.now(),
          first_name,
          last_name,
          email,
          sport_focus: cleanedSportFocus,
          hardware: cleanedHardware,
          other_hardware,
          polar_product,
          garmin_product,
          created_at: new Date().toISOString(),
        }),
      }
    );
    setIsSubmitting(false);

    if (!response.ok) {
      alert("Something went wrong. Please try again.");
    } else {
      alert("Thank you!");
      closeModal();
      setTesterFormData({
        first_name: "",
        last_name: "",
        email: "",
        sport_focus: [],
        hardware: [],
        other_hardware: "",
        polar_product: "",
        garmin_product: "",
      });
    }
  };

  const handleSubmitBuild = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { first_name, last_name, email, specialty, linkedin, cv_file } =
      buildFormData as any;

    let cv_url = "";

    if (cv_file) {
      const fileExt = cv_file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `cv_uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("resumes") // your storage bucket name
        .upload(filePath, cv_file);

      if (uploadError) {
        console.error("File upload failed:", uploadError.message);
        alert("Resume upload failed.");
        return;
      }

      const { data: publicUrlData } = supabase.storage
        .from("resumes")
        .getPublicUrl(filePath);

      cv_url = publicUrlData?.publicUrl ?? "";
    }

    const response = await fetch(
      "https://isxtaldeuhfbnlbnlmld.supabase.co/functions/v1/help_build_forward_to_n8n",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          id: Date.now(),
          first_name,
          last_name,
          email,
          specialty,
          linkedin,
          cv_url,
          created_at: new Date().toISOString(),
        }),
      }
    );
    setIsSubmitting(false);

    if (!response.ok) {
      alert("Something went wrong. Please try again.");
    } else {
      alert("Thank you!");
      closeModal();
      setBuildFormData({
        first_name: "",
        last_name: "",
        email: "",
        specialty: "",
        linkedin: "",
        cv_file: undefined,
      });
    }
  };

  return (
    <div className="bg-gradient-to-b from-[#0f0c1a] to-[#002b29] text-white font-sans min-h-screen">
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] pointer-events-auto cursor-wait">
          <div className="text-white text-xl font-semibold">Submitting...</div>
        </div>
      )}

      <div className="block md:hidden">
        <MobileLanding setModal={setModal} />
      </div>

      <div className="hidden md:block">
        <DesktopLanding setModal={setModal} />
      </div>

      {modal === "updates" && (
        <UpdatesModal
          formData={updatesFormData}
          onChange={handleChange}
          onClose={() => closeModal()}
          onSubmit={handleSubmitUpdates}
          isSubmitting={isSubmitting}
        />
      )}

      {modal === "tester" && (
        <TesterModal
          formData={testerFormData}
          onChange={handleChange}
          onClose={() => closeModal()}
          onSubmit={handleSubmitTester}
          isSubmitting={isSubmitting}
        />
      )}

      {modal === "build" && (
        <BuildModal
          formData={buildFormData}
          onChange={handleChange}
          onClose={() => closeModal()}
          onSubmit={handleSubmitBuild}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
